import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Scanner } from "@yudiel/react-qr-scanner";
import useDesktopPrintedQrBoost from "../../hooks/useDesktopPrintedQrBoost";
import useContinuousAutofocus from "../../hooks/useContinuousAutofocus";
import isMobileDevice from "../../utils/isMobileDevice";
import { isWasmQrScannerEnabled } from "../../configs/featureFlags";
import "./QrScannerView.css";

const isBenignScannerError = (error) => {
  const name = error?.name || "";
  const message =
    (error &&
      typeof error === "object" &&
      "message" in error &&
      error.message) ||
    String(error || "");

  return (
    name === "AbortError" ||
    /play\(\) request was interrupted/i.test(message) ||
    /media was removed from the document/i.test(message)
  );
};

const CAMERA_CONSTRAINTS = {
  facingMode: { ideal: "environment" },
  width: { ideal: 1920 },
  height: { ideal: 1080 },
  frameRate: { ideal: 30 },
};

const QR_FORMATS = ["qr_code"];

const SCANNER_STYLES = {
  container: {
    width: "100%",
    height: "100%",
    position: "relative",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
};

const QrScannerView = ({ onScan, onError, hintText }) => {
  const { t } = useTranslation();
  const isMobile = useMemo(() => isMobileDevice(), []);
  const containerRef = useRef(null);
  const wasmEnabled = isWasmQrScannerEnabled();

  const handleScan = useCallback(
    (detectedCodes) => {
      if (!detectedCodes?.length || !detectedCodes[0]?.rawValue) return;
      onScan(detectedCodes);
    },
    [onScan],
  );

  const handleError = useCallback(
    (error) => {
      if (isBenignScannerError(error)) return;
      onError?.(error);
    },
    [onError],
  );

  useContinuousAutofocus({
    containerRef,
    enabled: wasmEnabled,
  });

  useDesktopPrintedQrBoost({
    containerRef,
    enabled: wasmEnabled && !isMobile,
    onScan: handleScan,
  });

  const scannerComponents = useMemo(
    () => ({
      finder: false,
      onOff: false,
      torch: false,
      zoom: false,
      audio: false,
    }),
    [],
  );

  return (
    <div
      ref={containerRef}
      className={`qr-scanner-view overflow-hidden rounded-lg bg-black ${
        isMobile ? "qr-scanner-view--mobile" : "qr-scanner-view--desktop"
      }`}
    >
      <Scanner
        onScan={handleScan}
        onError={handleError}
        constraints={CAMERA_CONSTRAINTS}
        formats={QR_FORMATS}
        allowMultiple={false}
        scanDelay={0}
        components={scannerComponents}
        styles={SCANNER_STYLES}
      />

      {isMobile ? (
        <div className="absolute inset-0 pointer-events-none z-10">
          <div className="qr-scan-line-glow" />
          <div className="qr-scan-line" />

          <div className="qr-corner top-3 left-3 border-t-4 border-l-4 rounded-tl-lg border-cfPrimary" />
          <div
            className="qr-corner top-3 right-3 border-t-4 border-r-4 rounded-tr-lg border-cfPrimary"
            style={{ animationDelay: "0.5s" }}
          />
          <div
            className="qr-corner bottom-3 left-3 border-b-4 border-l-4 rounded-bl-lg border-cfPrimaryDark"
            style={{ animationDelay: "1s" }}
          />
          <div
            className="qr-corner bottom-3 right-3 border-b-4 border-r-4 rounded-br-lg border-cfPrimaryDark"
            style={{ animationDelay: "1.5s" }}
          />

          {hintText && (
            <p className="absolute bottom-4 left-0 right-0 text-center text-white text-xs sm:text-sm font-medium px-4 drop-shadow-md">
              {hintText}
            </p>
          )}
        </div>
      ) : (
        hintText && (
          <div className="absolute bottom-3 left-0 right-0 z-10 pointer-events-none text-center text-white px-4 drop-shadow-md">
            <p className="text-xs font-medium">{hintText}</p>
            <p className="text-[10px] sm:text-xs mt-1 opacity-90">
              {t("verify:scan_hint_desktop")}
            </p>
          </div>
        )
      )}
    </div>
  );
};

export default QrScannerView;
